import { z } from 'zod';
import { AuditedFields, CursorPageQuery, IsoDate, IsoDateTime, QueryBoolean, Uuid, Version, } from './common.js';
/** Catégorie fonctionnelle d'un type de ressource. */
export const ResourceCategory = z.enum(['LODGING', 'SPACE']);
/** Unité de tarification (ADR-009, D-006). */
export const PricingUnit = z.enum(['NIGHT', 'WEEK', 'MONTH', 'HOUR', 'HALF_DAY', 'DAY', 'PACKAGE']);
/** Statut opérationnel d'une ressource (FR-RES-05). */
export const OperationalStatus = z.enum([
    'AVAILABLE',
    'OCCUPIED',
    'DIRTY',
    'CLEANING',
    'READY',
    'MAINTENANCE',
    'BLOCKED',
]);
export const ResourceType = z
    .object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    category: ResourceCategory,
    defaultPricingUnit: PricingUnit,
    /** Un type système est seedé (STUDIO, APARTMENT, ROOM, MEETING_ROOM…) mais reste renommable. */
    isSystem: z.boolean(),
    sortOrder: z.number().int(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateResourceTypeRequest = z.object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,30}$/, 'Code en majuscules'),
    name: z.string().min(2).max(80),
    category: ResourceCategory,
    defaultPricingUnit: PricingUnit,
});
export const UpdateResourceTypeRequest = CreateResourceTypeRequest.omit({ code: true })
    .partial()
    .extend({ version: Version });
/** Caractéristique à cocher (D-019) : catalogue extensible par tenant. */
export const Amenity = z.object({
    code: z.string(),
    label: z.string(),
    icon: z.string().nullable(),
    sortOrder: z.number().int(),
    isActive: z.boolean(),
});
export const UpsertAmenityRequest = z.object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,40}$/),
    label: z.string().min(2).max(80),
    icon: z.string().max(40).nullable().optional(),
    sortOrder: z.number().int().optional(),
    isActive: z.boolean().optional(),
});
export const OpeningHours = z
    .object({
    /** Ex. { mon: [["08:00","18:00"]], sat: [] }. Absent = pas de contrainte. */
    mon: z.array(z.tuple([z.string(), z.string()])).optional(),
    tue: z.array(z.tuple([z.string(), z.string()])).optional(),
    wed: z.array(z.tuple([z.string(), z.string()])).optional(),
    thu: z.array(z.tuple([z.string(), z.string()])).optional(),
    fri: z.array(z.tuple([z.string(), z.string()])).optional(),
    sat: z.array(z.tuple([z.string(), z.string()])).optional(),
    sun: z.array(z.tuple([z.string(), z.string()])).optional(),
})
    .strict();
export const ResourcePhoto = z.object({
    documentId: Uuid,
    url: z.string().nullable(),
    caption: z.string().nullable(),
    sortOrder: z.number().int(),
});
export const InventoryUnit = z.object({ id: Uuid, code: z.string(), name: z.string() });
export const ResourceSummary = z.object({
    id: Uuid,
    name: z.string(),
    internalRef: z.string().nullable(),
    typeCode: z.string(),
    parentResourceId: Uuid.nullable(),
});
export const Resource = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    typeId: Uuid,
    typeCode: z.string(),
    typeName: z.string(),
    category: ResourceCategory,
    /** Ressource parente (ex. chambre → appartement). */
    parentResourceId: Uuid.nullable(),
    name: z.string(),
    internalRef: z.string().nullable(),
    description: z.string().nullable(),
    capacityStandard: z.number().int().nonnegative(),
    capacityMax: z.number().int().positive(),
    bedrooms: z.number().int().nonnegative().nullable(),
    bathrooms: z.number().int().nonnegative().nullable(),
    surfaceM2: z.number().nonnegative().nullable(),
    floor: z.number().int().nullable(),
    amenities: z.array(z.string()),
    photos: z.array(ResourcePhoto),
    minDuration: z.number().int().positive().nullable(),
    maxDuration: z.number().int().positive().nullable(),
    openingHours: OpeningHours.nullable(),
    operationalStatus: OperationalStatus,
    internalNotes: z.string().nullable(),
    isActive: z.boolean(),
    /** Date d'archivage ; une ressource archivée est en lecture seule (DEF-009). */
    archivedAt: IsoDateTime.nullable(),
    /**
     * Unités d'inventaire verrouillées par une réservation de cette ressource (ADR-013).
     * Feuille : sa propre unité ; composée : union des unités de ses enfants.
     */
    units: z.array(InventoryUnit),
    children: z.array(ResourceSummary),
    /** Tarif par défaut (plan actif `isDefault`) pour l'affichage rapide. */
    defaultRate: z
        .object({ pricingUnit: PricingUnit, price: z.number(), currencyCode: z.string().length(3) })
        .nullable(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateResourceRequest = z.object({
    propertyId: Uuid,
    typeId: Uuid,
    parentResourceId: Uuid.nullable().optional(),
    name: z.string().min(2).max(120),
    internalRef: z.string().max(40).nullable().optional(),
    description: z.string().max(4000).nullable().optional(),
    capacityStandard: z.number().int().nonnegative().default(1),
    capacityMax: z.number().int().positive(),
    bedrooms: z.number().int().nonnegative().nullable().optional(),
    bathrooms: z.number().int().nonnegative().nullable().optional(),
    surfaceM2: z.number().nonnegative().nullable().optional(),
    floor: z.number().int().nullable().optional(),
    amenities: z.array(z.string()).default([]),
    minDuration: z.number().int().positive().nullable().optional(),
    maxDuration: z.number().int().positive().nullable().optional(),
    openingHours: OpeningHours.nullable().optional(),
    internalNotes: z.string().max(4000).nullable().optional(),
});
export const UpdateResourceRequest = CreateResourceRequest.omit({ propertyId: true })
    .partial()
    .extend({
    isActive: z.boolean().optional(),
    version: Version,
});
export const UpdateOperationalStatusRequest = z.object({
    status: OperationalStatus,
    reason: z.string().max(500).optional(),
    version: Version,
});
export const ListResourcesQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    typeCode: z.string().optional(),
    category: ResourceCategory.optional(),
    q: z.string().max(100).optional(),
    includeInactive: QueryBoolean.default(false),
    /** Uniquement les ressources de premier niveau (les enfants sont imbriqués). */
    topLevelOnly: QueryBoolean.default(false),
});
/** Segment client d'un plan tarifaire. */
export const CustomerSegment = z.enum(['PUBLIC', 'CORPORATE']);
export const RatePlan = z
    .object({
    id: Uuid,
    resourceId: Uuid,
    name: z.string(),
    pricingUnit: PricingUnit,
    price: z.number().nonnegative(),
    currencyCode: z.string().length(3),
    validFrom: IsoDate.nullable(),
    validTo: IsoDate.nullable(),
    minQuantity: z.number().int().positive().nullable(),
    customerSegment: CustomerSegment,
    /** Tarif négocié pour une entreprise donnée (D-022) ; null = tarif général. */
    companyId: Uuid.nullable(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateRatePlanRequest = RatePlan.pick({
    name: true,
    pricingUnit: true,
    price: true,
    validFrom: true,
    validTo: true,
    minQuantity: true,
    customerSegment: true,
    companyId: true,
    isDefault: true,
}).partial({
    validFrom: true,
    validTo: true,
    minQuantity: true,
    companyId: true,
    isDefault: true,
    customerSegment: true,
});
export const UpdateRatePlanRequest = CreateRatePlanRequest.partial().extend({
    isActive: z.boolean().optional(),
    version: Version,
});
/** Blocage d'inventaire (FR-RES-04) ; EXTERNAL = importé par iCal. */
export const BlockKind = z.enum(['MAINTENANCE', 'ADMIN', 'OWNER', 'CLOSURE', 'EXTERNAL']);
export const ResourceBlock = z
    .object({
    id: Uuid,
    resourceId: Uuid,
    resourceName: z.string(),
    kind: BlockKind,
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    reason: z.string().nullable(),
    source: z.string().nullable(),
    externalUid: z.string().nullable(),
    createdByName: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateResourceBlockRequest = z
    .object({
    resourceId: Uuid,
    kind: BlockKind.exclude(['EXTERNAL']),
    startsAt: IsoDateTime,
    endsAt: IsoDateTime,
    reason: z.string().min(3).max(500),
    /** Forcer malgré des conflits futurs (permission availability.block + motif) : non, le conflit est refusé (409 RESOURCE_NOT_AVAILABLE). */
})
    .refine((v) => new Date(v.endsAt) > new Date(v.startsAt), {
    message: 'La fin doit être postérieure au début',
    path: ['endsAt'],
});
export const UpdateResourceBlockRequest = z.object({
    startsAt: IsoDateTime.optional(),
    endsAt: IsoDateTime.optional(),
    reason: z.string().min(3).max(500).optional(),
    version: Version,
});
export const ListResourceBlocksQuery = CursorPageQuery.extend({
    resourceId: Uuid.optional(),
    propertyId: Uuid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    kind: BlockKind.optional(),
});
/** Flux iCal externe (Booking.com, Airbnb) importé comme blocages EXTERNAL (E-19). */
export const ICalSource = z.enum(['BOOKING_COM', 'AIRBNB', 'OTHER']);
/**
 * Compte rendu de synchronisation iCal.
 * `status: 'ERROR'` signale un flux injoignable ou illisible — ce n'est pas une
 * erreur de l'appelant : la configuration du flux reste intacte et l'échec est
 * enregistré sur le flux (`lastSyncStatus`).
 */
export const ICalSyncReport = z.object({
    feedId: Uuid,
    status: z.enum(['OK', 'ERROR']),
    message: z.string().nullable(),
    imported: z.number().int().nonnegative(),
    created: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    /** Événements ignorés : conflit avec un blocage du PMS, ou VEVENT illisible. */
    skipped: z.array(z.object({ uid: z.string().nullable(), reason: z.string() })),
});
export const ICalFeed = z
    .object({
    id: Uuid,
    resourceId: Uuid,
    resourceName: z.string(),
    source: ICalSource,
    url: z.string().url(),
    isActive: z.boolean(),
    lastSyncAt: IsoDateTime.nullable(),
    lastSyncStatus: z.enum(['OK', 'ERROR']).nullable(),
    lastSyncMessage: z.string().nullable(),
    importedCount: z.number().int().nonnegative(),
    /**
     * Compte rendu détaillé de la synchronisation qui vient d'être déclenchée.
     * Présent uniquement dans la réponse de `POST /ical-feeds/:id/sync`, qui rend
     * le flux mis à jour (`{ data: ICalFeed }`) ; absent des listes.
     */
    lastSyncReport: ICalSyncReport.optional(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateICalFeedRequest = z.object({
    resourceId: Uuid,
    source: ICalSource,
    url: z.string().url().max(2048),
});
export const UpdateICalFeedRequest = z.object({
    url: z.string().url().max(2048).optional(),
    isActive: z.boolean().optional(),
    version: Version,
});
/**
 * Archivage d'une ressource (motif facultatif tracé dans l'audit).
 * `version` est **facultative** : le web archive depuis une liste, sans corps de
 * requête. Fournie, elle applique le verrou optimiste ; absente, le serveur
 * archive la version qu'il vient de lire.
 */
export const ArchiveResourceRequest = z.object({
    version: Version.optional(),
    reason: z.string().max(500).optional(),
});
export const ListAmenitiesQuery = z.object({
    includeInactive: QueryBoolean.default(false),
});
export const ListRatePlansQuery = z.object({
    includeInactive: QueryBoolean.default(false),
});
export const ListICalFeedsQuery = z.object({ resourceId: Uuid.optional() });
//# sourceMappingURL=resources.js.map